import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	AlertTriangle,
	Ban,
	CheckCircle2,
	FileSearch,
	Info,
	Link2,
	ListChecks,
	Loader2,
	ShieldAlert,
	Wrench,
} from "lucide-react";
import { technicalAuditService } from "@/services/technicalAuditService";
import {
	readSitemapPreview,
	SITEMAP_BLOCKER_COPY,
	SITEMAP_CONFIRM_CAUSES,
	SITEMAP_SITE_WIDE_CAUSES,
} from "@/types/technicalAudit";
import type {
	MissingFromSitemapDetails,
	PageIssue,
	SitemapApplyGate,
	SitemapCauseDetail,
	SitemapFixPayload,
	SitemapSeoPlugin,
	TechnicalAuditFixPreview,
} from "@/types/technicalAudit";

// missing_from_sitemap is an EVIDENCE issue, not a value diff: there is no
// "before text -> after text" to show. This card keeps the detector's evidence
// (live / indexable / internally linked / absent from a sitemap that DID list N
// URLs) and adds the diagnosed CAUSE, because the fix is cause-dependent:
//
//   auto_apply    stale sitemap cache, or a page-level noindex  -> Apply writes
//   needs_confirm a human excluded this page, or the whole post type is excluded
//                 site-wide, OR a theme/plugin filter excludes it and we can
//                 OVERRIDE that filter -> never applied without an explicit
//                 confirmation
//   refuse        a filter in theme/plugin CODE removes it and we cannot (or must
//                 not) override it — we show the exact file:line and refuse to
//                 edit their code — or the page simply cannot be in a sitemap
//                 (draft, password-protected, …)
//   not_an_issue  the row is a FALSE POSITIVE and there is nothing to apply:
//                 either the front/blog page (Yoast & Rank Math list the homepage
//                 in a separate sitemap) or `already_in_sitemap` — the preview
//                 re-read the LIVE sitemap and the URL is in it, so the crawl-time
//                 evidence on the row is stale.
//
// STALE EVIDENCE. `issue_details.in_sitemap` is what the CRAWLER saw, and it can
// be wrong (cached/partial sitemap at crawl time). The preview's live re-check is
// the authority, so the evidence strip below is DERIVED from the plan, never
// hardcoded: it must never assert "Not listed in the sitemap" for a page the
// backend just found listed, and it must offer no Apply for a non-problem.
//
// THE OVERRIDE (`override_available`). We can beat a theme/plugin filter WITHOUT
// editing the client's code: the plugin registers our own callback at
// PHP_INT_MAX and diffs the post ID back out of their exclusion array. Their
// code still runs — we just win the filter chain — and it is cleanly revertible.
// Three things this card must never blur:
//   1. It is a WORKAROUND, not a fix. The filter is untouched. The blamed
//      file:line stays the headline, and "remove this page from that array" stays
//      the recommendation.
//   2. The filter is probably NOT a bug. An indexable page excluded from the
//      sitemap by PHP is a state nobody reaches by accident through the SEO
//      plugin UI (Yoast's own "don't show in search results" toggle sets noindex
//      AND drops it from the sitemap) — somebody wrote that code on purpose, and
//      keeping low-value pages out of the sitemap is a mainstream crawl-budget
//      technique.
//   3. It overrides code somebody else owns, so it takes its OWN deliberate tick
//      (never pre-ticked) and WordPress admin will show a persistent notice
//      listing every page we override.
// When the backend withholds the override (`preflight_blockers`), the card says
// WHY — "this is your 404 template" is the single most useful thing it can say.
//
// The preview endpoint is the authority (is_auto_fixable === decision
// "auto_apply"); this card auto-loads it (mirrors HreflangCard) and reports an
// apply GATE upward so the drawer's Apply button can't fire on a refused row,
// and can't fire a SITE-WIDE change or an OVERRIDE until the user ticks the
// matching confirmation.

interface MissingFromSitemapCardProps {
	issue: PageIssue;
	/** Lets the drawer gate its Apply button on the diagnosed decision + the
	 *  user's explicit confirmation, and carry `confirm_site_wide` into the
	 *  apply body. Omitted in read-only contexts. */
	onGateChange?: (gate: SitemapApplyGate) => void;
}

const PLUGIN_LABELS: Record<SitemapSeoPlugin, string> = {
	yoast: "Yoast SEO",
	rankmath: "Rank Math",
	aioseo: "All in One SEO",
	core: "WordPress core",
	none: "your SEO plugin",
};

const pluginLabel = (plugin: SitemapSeoPlugin): string =>
	PLUGIN_LABELS[plugin] ?? "your SEO plugin";

// Fallback human name for the post type in blast-radius copy.
const postTypeLabel = (plan: SitemapFixPayload): string =>
	plan.blocking_cause.post_type || plan.post_type || "content type";

// Pluralize the post-type noun for "all 412 products".
const pluralPostType = (name: string): string =>
	name.endsWith("s") ? name : `${name}s`;

// Short headline per cause code, for the badge above the explanation.
const CAUSE_TITLES: Record<string, string> = {
	no_server_side_cause: "Stale sitemap cache",
	noindex: "The page is set to noindex",
	excluded_by_option: "Someone excluded this page by hand",
	post_type_excluded: "The whole content type is excluded site-wide",
	excluded_by_filter: "A filter in your site's code removes it",
	entry_filtered_out: "A filter in your site's code removes it",
	sql_filter_present: "Your site's code removes it from the sitemap's database query",
	post_type_filtered_out: "A filter in your site's code removes it",
	not_published: "The page isn't published",
	password_protected: "The page is password-protected",
	external_permalink: "The page points to an external URL",
	post_type_not_public: "The content type isn't public",
	front_or_blog_page: "This is your front page — not actually missing",
	already_in_sitemap: "Already in your sitemap — nothing to fix",
};

const causeTitle = (code: string): string =>
	CAUSE_TITLES[code] || code.replace(/_/g, " ");

// The cause codes we refuse on because the exclusion lives in THEIR code — we
// will not edit a client's theme/plugin files.
const CODE_FILTER_CAUSES = new Set<string>([
	"excluded_by_filter",
	"entry_filtered_out",
	"sql_filter_present",
	"post_type_filtered_out",
]);

// The exact explanation shown per cause. Written to be true for both what we
// will do (auto_apply / needs_confirm) and what we refuse to do.
const causeCopy = (plan: SitemapFixPayload): string => {
	const cause = plan.blocking_cause;
	const plugin = pluginLabel(plan.seo_plugin);
	// A filter we CAN outrank. Do not call it a mistake — an indexable page kept
	// out of the sitemap by PHP is a deliberate, mainstream crawl-budget move.
	if (plan.override_available && CODE_FILTER_CAUSES.has(cause.code)) {
		return "Your site's own code (a theme or plugin filter) deliberately excludes this page from the sitemap. That may well be intentional — keeping low-value pages out of the sitemap is a normal crawl-budget technique. If it's stale, you can override it below.";
	}
	switch (cause.code) {
		case "sql_filter_present":
			return "Your site's code removes this page from the database query the sitemap is built from, so it never becomes a sitemap candidate at all. We cannot override this one — an override works by out-prioritising a later filter, and here there is no later filter to outrank. Remove that SQL filter (or exempt this page from it).";
		case "no_server_side_cause":
			return "Nothing on the server excludes this page — the published sitemap is just stale (page cache / CDN). Apply clears the sitemap cache and re-checks the live sitemap.";
		case "noindex":
			return "This page is set to noindex, so your SEO plugin correctly leaves it out of the sitemap. Apply makes the page indexable again and re-checks the live sitemap.";
		case "excluded_by_option":
			return `Someone explicitly excluded this page in ${plugin}'s Exclude Posts list. Apply will remove it from that list.`;
		case "post_type_excluded": {
			const type = postTypeLabel(plan);
			const radius = plan.blast_radius;
			const count =
				typeof radius === "number" ? `all ${radius} ${pluralPostType(type)}` : `every ${type}`;
			return `Your '${type}' content type is excluded from the sitemap site-wide. Turning it back on adds ${count} to the sitemap AND makes them indexable in search — not just this page.`;
		}
		case "not_published":
			return "This page isn't published, so it cannot appear in the XML sitemap. Publish it (or exclude it from the audit) — we won't change its status for you.";
		case "password_protected":
			return "This page is password-protected. Password-protected content is deliberately kept out of sitemaps and search, so there is nothing safe to apply here.";
		case "external_permalink":
			return "This page's permalink points to an external URL, so it is not a real destination on your site and cannot be listed in the sitemap.";
		case "post_type_not_public":
			return "This page's content type is registered as non-public in your site's code, so it is never eligible for the sitemap or for search.";
		case "front_or_blog_page":
			return "This is your front page. Yoast/Rank Math list the homepage separately from the page sitemap, so it isn't actually missing — this is a false positive.";
		case "already_in_sitemap":
			return "We re-checked your live XML sitemap just now, and this page IS listed in it. Nothing is missing and there is nothing to apply — the audit row is out of date, because the crawl read a stale or incomplete copy of the sitemap. Skip this row (it clears on the next audit).";
		default:
			if (CODE_FILTER_CAUSES.has(cause.code)) {
				return "A filter in your site's code removes this page from the sitemap. We won't edit your theme/plugin code — remove that filter, or exclude this page from the audit.";
			}
			return (
				cause.detail ||
				"We couldn't pin down a specific server-side cause for this exclusion. Review it in WordPress before acting."
			);
	}
};

// The blamed file:line for a code-filter refusal — the single most valuable line
// in this whole card: it tells the client exactly which line of their code is
// doing it.
function BlamedCode({ cause }: { cause: SitemapCauseDetail }) {
	const blamed = cause.blamed ?? [];
	if (blamed.length === 0) return null;
	return (
		<div
			data-testid="sitemap-blamed-code"
			className="rounded-lg border border-destructive/30 bg-destructive/5 p-3"
		>
			<p className="text-xs font-semibold uppercase tracking-wide text-destructive">
				The code doing it
			</p>
			<div className="mt-2 space-y-1">
				{blamed.map((ref, idx) => (
					<code
						key={`${ref.file}-${ref.line}-${idx}`}
						className="block break-all text-xs font-semibold text-foreground"
					>
						{ref.file}:{ref.line}
						{ref.priority ? (
							<span className="ml-2 font-normal text-muted-foreground">
								(filter priority {ref.priority})
							</span>
						) : null}
					</code>
				))}
			</div>
			<p className="mt-2 text-xs text-destructive">
				Remove that filter (or exclude this page from the audit). We will not
				edit your theme or plugin code.
			</p>
		</div>
	);
}

export function MissingFromSitemapCard({
	issue,
	onGateChange,
}: MissingFromSitemapCardProps) {
	const details = (issue.issue_details || {}) as MissingFromSitemapDetails;
	const sitemapCount = details.sitemap_url_count;
	const inlinks = details.unique_inlinks ?? details.inlinks;
	const words = details.word_count;
	const title = details.page_title ?? details.title ?? issue.page_title;

	const [preview, setPreview] = useState<TechnicalAuditFixPreview | null>(null);
	const [plan, setPlan] = useState<SitemapFixPayload | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [loadError, setLoadError] = useState<string | null>(null);
	// The user's deliberate confirmation of a needs_confirm change. Never
	// pre-checked; reset whenever the diagnosed plan changes.
	const [confirmed, setConfirmed] = useState(false);
	// The OVERRIDE confirmation is a separate tick with its own wording: it does
	// not undo a setting, it overrides code somebody else wrote. Ticking the
	// site-wide box must never arm the override, or vice versa.
	const [overrideConfirmed, setOverrideConfirmed] = useState(false);

	// Auto-load the diagnosed plan so the cause is visible inline in the drawer
	// (mirrors HreflangCard/MultipleMetaCard).
	useEffect(() => {
		let ignore = false;
		Promise.resolve().then(() => {
			if (ignore) return;
			setIsLoading(true);
			setLoadError(null);
			setPreview(null);
			setPlan(null);
			setConfirmed(false);
			setOverrideConfirmed(false);

			technicalAuditService
				.previewFix(issue.id)
				.then((data) => {
					if (ignore) return;
					setPreview(data);
					setPlan(readSitemapPreview(data.planned_payload));
				})
				.catch((err: unknown) => {
					if (ignore) return;
					const detail =
						(
							err as {
								response?: { data?: { detail?: string } };
								message?: string;
							}
						).response?.data?.detail ||
						(err as { message?: string }).message ||
						"Failed to load the sitemap diagnosis";
					setLoadError(detail);
				})
				.finally(() => {
					if (!ignore) setIsLoading(false);
				});
		});

		return () => {
			ignore = true;
		};
	}, [issue.id]);

	const decision = plan?.decision ?? null;
	const cause = plan?.blocking_cause ?? null;
	const isAutoApply = decision === "auto_apply";
	const needsConfirm = decision === "needs_confirm";
	const isRefused = decision === "refuse";
	const isNotAnIssue = decision === "not_an_issue";
	// THE LIVE TRUTH about sitemap membership. The crawl-time evidence on the row
	// (`issue_details.in_sitemap`) is NOT trusted here: the preview re-reads the
	// published sitemap, and when it finds the URL, the row is stale and this whole
	// card must stop claiming the page is missing.
	const listedInSitemap = Boolean(
		plan?.already_in_sitemap || cause?.code === "already_in_sitemap",
	);
	// The matched entry, as positive evidence. (`matched_loc` normally comes back
	// on the membership probe; fall back to the URL we asked about.)
	const matchedLoc = plan?.matched_loc || (listedInSitemap ? issue.page_url : "");
	const matchedSitemap = plan?.sitemap_url || plan?.sitemap_index_url || "";
	// Site-wide = flipping a whole post type back on: the blast-radius case. Only
	// changes the WARNING copy — both confirmable causes are gated identically.
	const isSiteWide = Boolean(
		needsConfirm &&
			cause &&
			(SITEMAP_SITE_WIDE_CAUSES.has(cause.code) || cause.scope === "site"),
	);
	const blastRadius = plan?.blast_radius;
	const postType = plan ? postTypeLabel(plan) : "content type";

	// The FORCE-INCLUDE OVERRIDE is on offer: the backend diagnosed a theme/plugin
	// filter it can outrank (and found no preflight blocker). It is a needs_confirm
	// with its own, separate tick — it overrides code we don't own.
	const isOverride = Boolean(needsConfirm && plan?.override_available);
	// Already overridden by us. Revert removes the override (and the page drops
	// back out of the sitemap — their filter is untouched and back in sole charge).
	const forceIncluded = Boolean(plan?.force_included);
	// The override was WITHHELD: our own detector is wrong about this page (it is
	// the 404 template / noindex / a redirect / a duplicate / a WooCommerce page).
	const blockers = plan?.preflight_blockers ?? [];

	// The apply gate the drawer enforces. Until the preview resolves we report
	// canApply:false — nothing is applyable while the cause is unknown. A refused
	// or not_an_issue row is never applyable at all.
	const isConfirmable = Boolean(
		needsConfirm &&
			(isOverride || SITEMAP_CONFIRM_CAUSES.has(cause?.code ?? "")),
	);
	// Each confirmable path is armed ONLY by its own tick.
	const ticked = isOverride ? overrideConfirmed : confirmed;
	const canApply = isAutoApply || (isConfirmable && ticked);
	// The apply body's `confirm_site_wide` — ONE flag for the whole needs_confirm
	// path (a hand-excluded page, a site-wide post-type un-exclusion, AND the
	// force-include override). The BACKEND decides which write it authorises, from
	// its own live diagnosis; this is only ever "the human ticked the box". True
	// ONLY after the deliberate tick below, so a bulk "fix all" (which sends no
	// body) can never carry it.
	const confirmSiteWide = Boolean(isConfirmable && ticked);

	useEffect(() => {
		onGateChange?.({
			decision,
			canApply,
			confirmSiteWide,
		});
	}, [onGateChange, decision, canApply, confirmSiteWide]);

	return (
		<div className="space-y-4">
			<Card>
				<CardHeader className="pb-3">
					<CardTitle className="flex items-center gap-2 text-base">
						{listedInSitemap ? (
							<>
								<CheckCircle2 className="h-4 w-4 text-teal-dark" />
								This page is already in your XML sitemap
							</>
						) : (
							<>
								<FileSearch className="h-4 w-4 text-coral" />
								This page is not in your XML sitemap
							</>
						)}
					</CardTitle>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="rounded-md border bg-warm-bg p-3">
						<div className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
							Page
						</div>
						{title && (
							<div className="mb-1 text-sm font-medium text-foreground">
								{title}
							</div>
						)}
						<code className="break-all text-xs text-muted-foreground">
							{issue.page_url}
						</code>
					</div>

					{/* The detector's evidence — this is what makes it an issue rather
					    than a page that is correctly left out (a noindexed page, for
					    example, SHOULD be absent from a sitemap). Every line is DERIVED:
					    the "not listed" claim flips as soon as the live re-check says the
					    URL is in the sitemap. */}
					<div>
						<div className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
							{listedInSitemap ? "What we checked" : "Why this counts as a problem"}
						</div>
						<div
							data-testid="sitemap-evidence-strip"
							className="flex flex-wrap gap-2"
						>
							<Badge variant="outline" className="gap-1 border-teal/40 text-teal-dark">
								<ListChecks className="h-3 w-3" />
								Live (HTTP {issue.status_code ?? 200})
							</Badge>
							<Badge variant="outline" className="gap-1 border-teal/40 text-teal-dark">
								<ListChecks className="h-3 w-3" />
								Indexable
							</Badge>
							{typeof inlinks === "number" && (
								<Badge variant="outline" className="gap-1 border-teal/40 text-teal-dark">
									<Link2 className="h-3 w-3" />
									{inlinks} internal link{inlinks === 1 ? "" : "s"}
								</Badge>
							)}
							{typeof words === "number" && (
								<Badge variant="outline" className="gap-1">
									{words} words
								</Badge>
							)}
							{/* The membership line, DERIVED. Three states, and "not listed" is
							    only ever asserted once the live re-check has come back and
							    disagreed with nothing. While the check is in flight we say we
							    are checking rather than repeat the (possibly stale) crawl. */}
							{listedInSitemap ? (
								<Badge
									data-testid="sitemap-listed-badge"
									variant="outline"
									className="gap-1 border-teal/50 bg-teal-light text-teal-dark"
								>
									<CheckCircle2 className="h-3 w-3" />
									Listed in the sitemap
								</Badge>
							) : isLoading ? (
								<Badge variant="outline" className="gap-1 text-muted-foreground">
									<Loader2 className="h-3 w-3 animate-spin" />
									Re-checking the live sitemap…
								</Badge>
							) : (
								<Badge variant="outline" className="gap-1 border-coral/50 text-coral-dark">
									Not listed in the sitemap
									{typeof sitemapCount === "number"
										? ` (it lists ${sitemapCount} URLs)`
										: ""}
								</Badge>
							)}
						</div>
						{listedInSitemap ? (
							<p className="mt-3 text-sm text-muted-foreground">
								This URL is present in your published XML sitemap right now, so
								Google is already being told about it. The audit row is stale — the
								crawl read a cached or incomplete sitemap.
							</p>
						) : (
							<p className="mt-3 text-sm text-muted-foreground">
								Google can still find this page by following internal links, but it
								is never explicitly submitted — so it is discovered and recrawled
								more slowly, and can be treated as lower priority.
							</p>
						)}
					</div>
				</CardContent>
			</Card>

			{isLoading && (
				<div className="flex items-center gap-2 rounded-xl border border-border bg-warm-bg p-4 text-sm text-foreground/70">
					<Loader2 className="h-4 w-4 animate-spin" />
					Checking your server for what is excluding this page…
				</div>
			)}

			{!isLoading && plan && cause && (
				<Card
					data-testid="sitemap-diagnosis"
					className={
						isRefused
							? "border-destructive/30 shadow-sm"
							: isNotAnIssue
								? "border-border shadow-sm"
								: needsConfirm
									? "border-coral/40 shadow-sm"
									: "border-teal/30 bg-teal-light/40 shadow-sm"
					}
				>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-sm font-semibold text-foreground">
							<div className="flex h-8 w-8 items-center justify-center rounded-lg bg-teal-light">
								{isRefused ? (
									<Ban className="h-4 w-4 text-destructive" />
								) : isNotAnIssue ? (
									<Info className="h-4 w-4 text-teal-dark" />
								) : needsConfirm ? (
									<AlertTriangle className="h-4 w-4 text-coral-dark" />
								) : (
									<Wrench className="h-4 w-4 text-teal-dark" />
								)}
							</div>
							What we found
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3 pt-0">
						<div className="flex flex-wrap items-center gap-2">
							<Badge
								data-testid="sitemap-cause-badge"
								variant="outline"
								className={
									isRefused
										? "border-destructive/30 bg-destructive/5 text-destructive"
										: needsConfirm
											? "border-coral/40 bg-coral-light text-coral-dark"
											: "border-teal/40 bg-teal-light text-teal-dark"
								}
							>
								{causeTitle(cause.code)}
							</Badge>
							{plan.seo_plugin !== "none" && (
								<Badge variant="outline" className="text-muted-foreground">
									{pluginLabel(plan.seo_plugin)}
								</Badge>
							)}
						</div>

						<p
							data-testid="sitemap-cause-copy"
							className="text-sm text-foreground/80"
						>
							{causeCopy(plan)}
						</p>

						{cause.detail && (
							<p className="text-xs text-muted-foreground">{cause.detail}</p>
						)}

						{/* Already overridden by us — show it, and what Revert does. */}
						{forceIncluded && (
							<div
								data-testid="sitemap-force-included"
								className="rounded-lg border border-teal/40 bg-teal-light/60 p-3"
							>
								<p className="text-xs font-semibold uppercase tracking-wide text-teal-dark">
									Force-included by us
								</p>
								<p className="mt-1 text-xs text-foreground/80">
									We are already overriding your site's filter for this page, so
									it is listed in the sitemap even though your code excludes it.
									Your filter is untouched, and WordPress admin shows a notice
									listing every page we override. <strong>Revert</strong> removes
									our override — the page then drops back out of the sitemap.
								</p>
							</div>
						)}

						{/* REFUSE — the exclusion is in their code (or the page can never be
						    in a sitemap). Show the blamed file:line prominently. */}
						{isRefused && (
							<>
								<BlamedCode cause={cause} />
								{blockers.length > 0 && (
									<div
										data-testid="sitemap-override-blocked"
										className="rounded-lg border border-destructive/40 bg-destructive/5 p-3"
									>
										<p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-destructive">
											<ShieldAlert className="h-3.5 w-3.5 shrink-0" />
											We will not force this page into the sitemap
										</p>
										<p className="mt-1 text-xs text-foreground/80">
											We could out-prioritise that filter — but we won't, because{" "}
											{blockers
												.map(
													(code) =>
														SITEMAP_BLOCKER_COPY[code] ??
														`your site flagged “${code.replace(/_/g, " ")}”`,
												)
												.join("; and ")}
											.
										</p>
									</div>
								)}
								<div
									data-testid="sitemap-refusal"
									className="rounded-lg border border-destructive/30 bg-destructive/5 p-3"
								>
									<p className="text-xs font-medium text-destructive">
										{plan.refusal_reason ||
											"We won't change this automatically — the change isn't ours to make safely."}
									</p>
									<p className="mt-1 text-xs text-muted-foreground">
										Apply does not change WordPress for this row. Fix it at the
										source, or skip the issue.
									</p>
								</div>
							</>
						)}

						{/* NOT AN ISSUE — a false positive: the front/blog page, or a page the
						    live re-check found in the sitemap after all. ONE presentation for
						    both; only the reason differs. No apply affordance, ever. */}
						{isNotAnIssue && (
							<div
								data-testid="sitemap-not-an-issue"
								className="rounded-lg border border-teal/30 bg-teal-light/60 p-3"
							>
								<p className="flex items-center gap-1.5 text-xs font-medium text-teal-dark">
									<CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
									No action needed — skip this row.
								</p>
								<p className="mt-1 text-xs text-muted-foreground">
									{listedInSitemap
										? "There is no change to make in WordPress: this page is in your XML sitemap. The audit row is out of date and clears on the next crawl."
										: "There is no change to make in WordPress: the page is already covered by a different sitemap."}
								</p>
							</div>
						)}

						{/* The positive evidence for `already_in_sitemap`: the sitemap we read,
						    and the entry we matched in it. */}
						{listedInSitemap && (matchedSitemap || matchedLoc) && (
							<div
								data-testid="sitemap-listed-evidence"
								className="rounded-lg border border-teal/30 bg-card p-3"
							>
								<p className="text-xs font-semibold uppercase tracking-wide text-teal-dark">
									What we found in your sitemap
								</p>
								{matchedSitemap && (
									<p className="mt-2 text-xs text-muted-foreground">
										Sitemap we checked:{" "}
										<code className="break-all text-foreground">{matchedSitemap}</code>
									</p>
								)}
								{matchedLoc && (
									<p className="mt-1 text-xs text-muted-foreground">
										Matching entry:{" "}
										<code className="break-all text-foreground">{matchedLoc}</code>
									</p>
								)}
							</div>
						)}

						{/* NEEDS CONFIRM (override) — a theme/plugin filter excludes the page
						    and we CAN outrank it without touching their code. The blamed
						    file:line stays the headline and "remove that exclusion" stays the
						    recommendation; the override is the stopgap, clearly separated and
						    behind its own tick. */}
						{isOverride && (
							<>
								<BlamedCode cause={cause} />
								<div
									data-testid="sitemap-override-offer"
									className="rounded-lg border border-coral/40 bg-coral-light p-3"
								>
									<p className="flex items-center gap-1.5 text-sm font-semibold text-coral-dark">
										<ShieldAlert className="h-4 w-4 shrink-0" />
										Or force it into the sitemap anyway
									</p>
									<p className="mt-1 text-xs text-foreground/80">
										This does <strong>not</strong> remove their filter. We add a
										higher-priority override, so your page is listed even though
										your site's own code says to exclude it. It is a workaround,
										not a fix — the root-cause fix is still to remove this page
										from that exclusion.
									</p>
									<p className="mt-2 text-xs text-foreground/80">
										A persistent notice will appear in WordPress admin listing
										every page we override, and the sitemap carries an XML
										comment, so your developers can see exactly what we did and
										undo it. Revert removes the override at any time.
									</p>
								</div>
								<label
									className="flex cursor-pointer items-start gap-2 rounded-lg border border-coral/40 bg-coral-light p-3"
									htmlFor={`sitemap-override-confirm-${issue.id}`}
								>
									<input
										id={`sitemap-override-confirm-${issue.id}`}
										data-testid="sitemap-override-confirm-checkbox"
										type="checkbox"
										checked={overrideConfirmed}
										onChange={(e) => setOverrideConfirmed(e.target.checked)}
										className="mt-0.5 h-4 w-4 shrink-0 accent-coral"
									/>
									<span className="text-xs text-coral-dark">
										I understand this overrides code in my site and does not
										remove the filter.
									</span>
								</label>
								{!overrideConfirmed && (
									<p
										data-testid="sitemap-confirm-hint"
										className="text-xs text-muted-foreground"
									>
										Apply stays disabled until you confirm.
									</p>
								)}
							</>
						)}

						{/* NEEDS CONFIRM — either a human excluded this page by hand, or the
						    whole post type is off site-wide (the dangerous one). Neither is
						    ever applied without the deliberate tick below. */}
						{needsConfirm && !isOverride && (
							<>
								{isSiteWide && (
									<div
										data-testid="sitemap-blast-radius"
										className="rounded-lg border border-destructive/40 bg-destructive/5 p-3"
									>
										<p className="flex items-center gap-1.5 text-sm font-semibold text-destructive">
											<AlertTriangle className="h-4 w-4 shrink-0" />
											This is a site-wide change, not a one-page fix
										</p>
										<p className="mt-1 text-xs text-destructive">
											{typeof blastRadius === "number" ? (
												<>
													Applying this adds{" "}
													<strong>
														all {blastRadius} {pluralPostType(postType)}
													</strong>{" "}
													to the sitemap and makes them indexable in Google
													search — not just this page.
												</>
											) : (
												<>
													Applying this adds{" "}
													<strong>every {postType}</strong> on the site to the
													sitemap and makes them indexable in Google search —
													not just this page.
												</>
											)}
										</p>
									</div>
								)}
								<label
									className="flex cursor-pointer items-start gap-2 rounded-lg border border-coral/40 bg-coral-light p-3"
									htmlFor={`sitemap-confirm-${issue.id}`}
								>
									<input
										id={`sitemap-confirm-${issue.id}`}
										data-testid="sitemap-confirm-checkbox"
										type="checkbox"
										checked={confirmed}
										onChange={(e) => setConfirmed(e.target.checked)}
										className="mt-0.5 h-4 w-4 shrink-0 accent-coral"
									/>
									<span className="text-xs text-coral-dark">
										{isSiteWide ? (
											<>
												I understand this changes{" "}
												<strong>
													{typeof blastRadius === "number"
														? `all ${blastRadius} ${pluralPostType(postType)}`
														: `every ${postType}`}
												</strong>{" "}
												site-wide, not only this page, and I want to apply it.
											</>
										) : (
											<>
												I understand someone deliberately excluded this page,
												and I want Apply to remove it from{" "}
												{pluginLabel(plan.seo_plugin)}&apos;s Exclude Posts
												list.
											</>
										)}
									</span>
								</label>
								{!confirmed && (
									<p
										data-testid="sitemap-confirm-hint"
										className="text-xs text-muted-foreground"
									>
										Apply stays disabled until you confirm.
									</p>
								)}
							</>
						)}

						{/* AUTO APPLY — plain "what Apply will do". */}
						{isAutoApply && (
							<div
								data-testid="sitemap-auto-apply"
								className="rounded-lg border border-teal/30 bg-card p-3"
							>
								<p className="text-xs font-semibold uppercase tracking-wide text-teal-dark">
									What Apply will do
								</p>
								<p className="mt-1 text-sm text-foreground/80">
									{cause.fix ||
										(cause.code === "noindex"
											? "Apply sets this page to index (Yoast/Rank Math robots meta), clears the sitemap cache, then re-checks the live sitemap to confirm the URL now appears."
											: "Apply clears the sitemap cache, then re-checks the live sitemap to confirm the URL now appears.")}
								</p>
							</div>
						)}

						{/* (Already shown, with the matched loc, in the listed-evidence block.) */}
						{plan.sitemap_index_url && !listedInSitemap && (
							<p className="text-xs text-muted-foreground">
								Sitemap checked:{" "}
								<code className="break-all">{plan.sitemap_index_url}</code>
							</p>
						)}

						{/* Everything else the scan found, when the blocking cause isn't the
						    only one — useful when fixing the blocker exposes the next. */}
						{plan.all_causes.length > 1 && (
							<div data-testid="sitemap-other-causes">
								<p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
									Also found
								</p>
								<ul className="ml-4 mt-1 list-disc space-y-1 text-xs text-muted-foreground">
									{plan.all_causes
										.filter((c) => c.code !== cause.code)
										.map((c, idx) => (
											<li key={`${c.code}-${idx}`}>
												<span className="font-medium">{causeTitle(c.code)}</span>
												{c.detail ? ` — ${c.detail}` : ""}
											</li>
										))}
								</ul>
							</div>
						)}
					</CardContent>
				</Card>
			)}

			{/* No diagnosis available (fetch failed, or a legacy row previewed before
			    the sitemap plan existed): fall back to the manual checklist rather
			    than claiming an automatic fix we can't stand behind. */}
			{!isLoading && !plan && (
				<Card>
					<CardHeader className="pb-3">
						<CardTitle className="flex items-center gap-2 text-base">
							<Wrench className="h-4 w-4 text-coral" />
							How to fix it
						</CardTitle>
					</CardHeader>
					<CardContent className="space-y-3">
						<p className="text-sm text-muted-foreground">
							{loadError
								? "We couldn't diagnose the cause automatically. The correct change depends on why the page is being left out — check these in order:"
								: "The correct change depends on why the page is being left out. Check these in order:"}
						</p>
						<ol className="ml-4 list-decimal space-y-1.5 text-sm text-muted-foreground">
							<li>
								Is the page set to <strong>noindex</strong> in Yoast / Rank Math? If
								so it is correctly excluded — either make it indexable, or accept it.
							</li>
							<li>
								Does the SEO plugin exclude this <strong>post type</strong> from the
								sitemap? (Yoast → Search Appearance → Content Types → “Show in search
								results”.)
							</li>
							<li>
								Is there an <strong>exclusion filter</strong> or plugin removing this
								specific page?
							</li>
							<li>
								Is the <strong>sitemap cache stale</strong>? Clear it in the SEO
								plugin and re-check.
							</li>
						</ol>
						<p className="text-sm text-muted-foreground">
							Once the page appears in the XML sitemap, re-submit the sitemap in
							Google Search Console to speed up discovery.
						</p>
					</CardContent>
				</Card>
			)}

			{/* Belt and braces: a preview that came back NOT auto-fixable and NOT a
			    confirmable row must never look applyable. (A not_an_issue row already
			    says "no action needed" in its own calm block — a coral review-only
			    warning on top of it would re-frame a non-problem as a problem.) */}
			{!isLoading &&
				preview &&
				!preview.is_auto_fixable &&
				!needsConfirm &&
				!isNotAnIssue &&
				plan && (
				<div className="rounded-md border border-coral/30 bg-coral-light p-3 text-sm text-coral-dark">
					Review only — Apply does not change WordPress for this issue.
				</div>
			)}
		</div>
	);
}
